import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMapPin, LuCheck, LuSearch, LuPlus, LuTriangleAlert, LuX, LuUserPlus } from 'react-icons/lu';
import api, { setComoEmpresa } from '../../services/api';
import UbicacionEnMapa from '../mapa/UbicacionEnMapa';
import ModalShell from '../ui/ModalShell';
import { StepBar } from '../servicio/ServicioUI';
import { MOTIVOS_PEDIDO } from '../../utils/pedidosEmpresa';
import { limpiarSerie } from '../../utils/serie';
import { CargarFotos } from './FotosPedido';
import ElegirVentanas from './ElegirVentanas';
import { diaLargo, idSemana } from './diasRuta';
import ElegirDiaPedido from './ElegirDiaPedido';
import useBorrador from '../../hooks/useBorrador';
import { normalizarVentanas } from '../../utils/franjas';
import AvisoBorrador from '../ui/AvisoBorrador';
import { getTodayISO } from '../../utils/dateUtils';
import { AvisoParecidos, CodigoYOrigen } from './DatosClienteEmpresa';

const INPUT = 'w-full h-11 px-3.5 rounded-xl bg-chip text-body text-ink outline-none placeholder:text-muted';
const ROTULO = 'text-label font-black uppercase tracking-widest text-muted';
const CLIENTE_VACIO = { nombre: '', codigo: '', origen: 'INSTALACION', instaladoEl: '', calle: '', numero: '', piso: '', depto: '', localidad: '', notas: '' };
let sigClave = 1;
const equipoNuevo = (datos = {}) => ({ clave: sigClave++, serie: '', modelo: '', ubicacion: '', motivo: '', deLista: false, conSerie: false, ...datos });
// Un pedido a medio cargar que vale la pena recuperar (20-oct-2026)
const pedidoConContenido = (d) => !!(d && ((d.nuevo && (d.cliente?.nombre?.trim() || d.cliente?.calle?.trim()))
    || d.equipos?.some(e => e.motivo || e.serie || e.modelo) || d.detalle?.trim() || d.fotos?.length || d.ventanas?.length));


// Nuevo pedido (Portal Empresa). Carga guiada (10-oct-2026), como la carga de trabajo del admin:
// 1) ¿para quién? — uno de sus clientes (lugares) o uno nuevo, que queda guardado;
// 2) los equipos, cada uno con lo que le pasa; 3) cuándo pueden recibir al técnico.
// soloMiLugar: encargado de un lugar — no elige cliente, arranca en los equipos.
// editar (10-oct-2026): un pedido todavía sin agendar — arranca en los equipos con lo
// que tenía; el lugar no se cambia.
export default function NuevoPedidoSheet({ onCerrar, onCreado, inicial = null, soloMiLugar = false, editar = null, puedeRuta = false, comoEmpresa = null }) {
    // comoEmpresa ({ id, nombre }): el admin lo carga a nombre de la empresa (29-oct-2026). Se fija antes del
    // primer render para que la primera llamada ya salga con el header.
    const comoId = comoEmpresa?.id || null;
    useState(() => { setComoEmpresa(comoId); return null; });
    useEffect(() => { setComoEmpresa(comoId); return () => setComoEmpresa(null); }, [comoId]);
    const [sedes, setSedes] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [q, setQ] = useState('');
    const [sedeId, setSedeId] = useState(editar?.sedeId || inicial?.sedeId || null);
    const [nuevo, setNuevo] = useState(false);
    const [cliente, setCliente] = useState(CLIENTE_VACIO);
    // Nombre igual o parecido a otro cliente (21-oct-2026): ¿otra dirección suya o un cliente distinto?
    const [decision, setDecision] = useState(null);
    const [parecidos, setParecidos] = useState([]);
    const setDato = (k) => (v) => setCliente(c => ({ ...c, [k]: v }));
    const [equipos, setEquipos] = useState(() => {
        if (editar) {
            const l = editar.equipos?.length ? editar.equipos
                : [{ serie: editar.equipoSerie, motivo: editar.motivo }];
            return l.map(e => equipoNuevo({ serie: e.serie || '', modelo: e.modelo || '', ubicacion: e.ubicacion || '',
                motivo: e.motivo || '', deLista: false }));
        }
        // Varios equipos elegidos en la lista (11-oct-2026)
        if (inicial?.series?.length)
            return inicial.series.map(serie => equipoNuevo({ serie, motivo: inicial.motivo || '', deLista: true }));
        return inicial?.serie || inicial?.motivo
            ? [equipoNuevo({ serie: inicial.serie || '', motivo: inicial.motivo || '', deLista: !!inicial.serie })] : [];
    });
    const [ventanas, setVentanas] = useState(normalizarVentanas(editar?.ventanas || []));
    const [detalle, setDetalle] = useState(editar?.detalle || '');
    const [fotos, setFotos] = useState(editar?.fotos || []);
    const [urgente, setUrgente] = useState(!!editar?.urgente);
    // ¿Para qué día? (23-oct-2026): con día, el pedido entra solo en la ruta de ese día
    // ¿Para qué día? (25-oct-2026): del calendario de zonas, para cualquier usuario de la empresa
    const conDia = !editar;
    const [rutaFecha, setRutaFecha] = useState(null);
    const diaSinVentana = rutaFecha && ventanas.length > 0 && !ventanas.some(v => v.dia === idSemana(rutaFecha));
    const [guardando, setGuardando] = useState(false);
    const [paso, setPaso] = useState(soloMiLugar || editar || inicial?.sedeId ? 1 : 0);
    const arriba = useRef(null);

    useEffect(() => {
        api.get('/empresa/sedes').then(r => {
            const l = Array.isArray(r.data) ? r.data : [];
            setSedes(l);
            if (l.length === 1 && soloMiLugar) setSedeId(l[0].id);
            if (l.length === 0 && !soloMiLugar) setNuevo(true);
        }).catch(() => { if (!soloMiLugar) setNuevo(true); }).finally(() => setCargando(false));
    }, [soloMiLugar]);

    // Cada paso arranca arriba
    useEffect(() => { arriba.current?.scrollIntoView({ block: 'start' }); }, [paso]);

    const sede = sedes.find(s => s.id === sedeId) || null;
    const equiposDelLugar = useMemo(() => (nuevo ? [] : sede?.equipos || []), [nuevo, sede]);

    // Desde la ficha de un equipo: completa modelo y ubicación cuando llegan los lugares
    useEffect(() => {
        if (!sede) return;
        setEquipos(es => es.map(e => {
            // Al editar, los N/S que ya son equipos del lugar se muestran como elegidos de la lista
            const d = (e.deLista || (editar && e.serie)) && (sede.equipos || []).find(x => x.serie === e.serie);
            return d ? { ...e, deLista: true, modelo: e.modelo || d.modelo || '', ubicacion: e.ubicacion || d.ubicacion || '' } : e;
        }));
    }, [sede, editar]);

    // Si no hay equipos cargados para elegir, el paso 2 arranca con uno en blanco
    useEffect(() => {
        if (paso === 1 && equipos.length === 0 && equiposDelLugar.length === 0) setEquipos([equipoNuevo()]);
    }, [paso, equipos.length, equiposDelLugar.length]);

    const filtradas = useMemo(() => {
        const norm = (x) => String(x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const palabras = norm(q).split(/\s+/).filter(Boolean);
        if (!palabras.length) return sedes;
        return sedes.filter(s => {
            const texto = norm([s.codigo, s.nombre, s.direccion, ...(s.series || [])].filter(Boolean).join(' '));
            return palabras.every(p => texto.includes(p));
        });
    }, [q, sedes]);

    const [puntoNuevo, setPuntoNuevo] = useState(null);   // punto del mapa en vivo del cliente nuevo

    // Borrador (20-oct-2026): si salís de la app (por ejemplo a sacar una foto) y el celu recarga la
    // página, al volver ofrece seguir con todo: el cliente nuevo, los equipos, el paso y las fotos.
    const borrador = useBorrador({
        nombre: comoId ? `pedido_borrador_admin_${comoId}` : soloMiLugar ? 'pedido_borrador_lugar' : 'pedido_borrador',
        activo: !editar,
        estado: { nuevo, cliente, sedeId, equipos, ventanas, detalle, fotos, urgente, paso, puntoNuevo },
        conContenido: pedidoConContenido,
        aplicar: (d) => {
            setNuevo(!!d.nuevo); setCliente({ ...CLIENTE_VACIO, ...(d.cliente || {}) }); setSedeId(d.sedeId ?? null);
            const eqs = Array.isArray(d.equipos) ? d.equipos : [];
            sigClave = Math.max(sigClave, ...eqs.map(e => (e.clave || 0) + 1));
            setEquipos(eqs); setVentanas(d.ventanas || []); setDetalle(d.detalle || ''); setFotos(d.fotos || []);
            setUrgente(!!d.urgente); setPuntoNuevo(d.puntoNuevo || null);
            if (Number.isInteger(d.paso)) setPaso(d.paso);
        },
    });
    const nombreBorrador = borrador.ofrecido
        ? (borrador.ofrecido.nuevo ? borrador.ofrecido.cliente?.nombre?.trim() : sedes.find(s => s.id === borrador.ofrecido.sedeId)?.nombre) : '';
    const clienteListo = cliente.nombre.trim().length >= 2 && cliente.calle.trim() && cliente.localidad.trim();
    const paso1Listo = nuevo ? clienteListo && (!parecidos.length || !!decision) : !!sedeId;
    const paso2Listo = equipos.length > 0 && equipos.every(e => e.motivo);
    const ultimoMotivo = () => [...equipos].reverse().find(e => e.motivo)?.motivo || '';

    const elegirLugar = (id) => {
        if (id !== sedeId) setEquipos([]);
        setSedeId(id);
        setPaso(1); // un toque y sigue
    };
    const alternarDeLista = (eq) => {
        const ya = equipos.find(e => e.deLista && e.serie === eq.serie);
        if (ya) setEquipos(es => es.filter(e => e !== ya));
        else setEquipos(es => [...es.filter(e => e.deLista || e.serie || e.modelo || e.ubicacion || e.motivo),
            equipoNuevo({ serie: eq.serie, modelo: eq.modelo || '', ubicacion: eq.ubicacion || '', motivo: ultimoMotivo(), deLista: true })]);
    };
    const cambiarEquipo = (clave, cambios) => setEquipos(es => es.map(e => (e.clave === clave ? { ...e, ...cambios } : e)));

    const enviar = async () => {
        if (!paso2Listo) { setPaso(1); toast.error('Elegí qué le pasa a cada equipo'); return; }
        // 1-nov-2026: cargado por el admin, los días son opcionales (si todavía no se sabe, queda Por agendar)
        if (ventanas.length === 0 && !comoEmpresa) { toast.error('Marcá cuándo pueden recibirnos'); return; }
        setGuardando(true);
        try {
            const limpio = (v) => (v || '').trim() || null;
            const cuerpo = {
                sedeId: nuevo ? null : sedeId,
                clienteNuevo: nuevo ? { ...Object.fromEntries(Object.entries(cliente).map(([k, v]) => [k, limpio(v)])),
                    // Si no puso fecha, el cliente nuevo se instala el día de la visita (si eligió uno)
                    instaladoEl: limpio(cliente.instaladoEl) || (conDia && rutaFecha) || getTodayISO(),
                    ...(decision?.tipo === 'otra' ? { otraDe: decision.cliente.id } : {}),
                    ...(decision?.tipo === 'distinto' ? { distinto: true } : {}),
                    // Dónde cae en el mapa (lo vio y quizá lo corrigió): así lo encontramos para ir
                    ...(puntoNuevo ? { lat: puntoNuevo.lat, lng: puntoNuevo.lng } : {}) } : null,
                equipos: equipos.map(e => ({ serie: limpio(e.serie), modelo: limpio(e.modelo), ubicacion: limpio(e.ubicacion), motivo: e.motivo })),
                ventanas,
                detalle: limpio(detalle),
                urgente,
                fotos,
                ...(conDia && rutaFecha ? { rutaFecha } : {}),
            };
            const r = editar ? await api.put(`/empresa/pedidos/${editar.id}`, cuerpo) : await api.post('/empresa/pedidos', cuerpo);
            if (!editar) borrador.limpiar();
            const res = r.data?.reserva;
            toast.success(editar ? 'Pedido actualizado'
                : res?.modo === 'ENTRA' ? `Pedido agendado para el ${diaLargo(res.fecha)}`
                : res?.modo === 'PEDIDO' ? 'Pedido enviado: se lo pedimos a Dispenser La Tienda para mañana'
                : res?.modo === 'RESERVA' ? `Pedido reservado para el ${diaLargo(res.fecha)}` : 'Pedido enviado');
            onCreado && onCreado(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.mensaje || e?.response?.data?.message || 'No se pudo enviar el pedido');
        } finally { setGuardando(false); }
    };

    // Pasos visibles: el encargado de un lugar no elige cliente
    const pasos = soloMiLugar || editar ? [1, 2] : [0, 1, 2];
    const indice = pasos.indexOf(paso);
    const TITULOS = ['¿Para quién es?', '¿Qué equipos?', '¿Cuándo pueden recibirnos?'];
    const nombreLugar = nuevo ? cliente.nombre.trim() : (sede?.nombre || sede?.direccion);
    // Para que la app ofrezca los días de su zona: el lugar (o la dirección del cliente nuevo) y el trabajo
    const consultaDia = {
        ...(nuevo ? { direccion: [[cliente.calle, cliente.numero].filter(Boolean).join(' '), cliente.localidad].filter(Boolean).join(', ') } : { sedeId }),
        motivo: equipos[0]?.motivo || 'Otro', equipos: Math.max(equipos.length, 1), ventanas,
    };

    const pie = (
        <div className="flex gap-2">
            {indice > 0 && (
                <button type="button" onClick={() => setPaso(pasos[indice - 1])}
                    className="flex-1 h-12 rounded-xl bg-chip text-secondary font-black text-body active:scale-95">Atrás</button>
            )}
            {paso < 2 ? (
                <button type="button" onClick={() => setPaso(paso + 1)} disabled={paso === 0 ? !paso1Listo : !paso2Listo}
                    className="flex-[2] h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">Siguiente</button>
            ) : (
                <button type="button" onClick={enviar} disabled={(ventanas.length === 0 && !comoEmpresa) || guardando}
                    className="flex-[2] h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">{guardando ? 'Guardando…' : editar ? 'Guardar cambios' : 'Enviar pedido'}</button>
            )}
        </div>
    );

    return (
        <ModalShell titulo={editar ? `Editar pedido #${editar.id}` : comoEmpresa ? 'Pedido para agendar' : 'Nuevo pedido'}
            subtitulo={editar ? 'Mientras no esté agendado podés cambiarlo' : comoEmpresa ? `${comoEmpresa.nombre} · hay que hacerlo, va al cierre del mes` : 'Cargalo y elegí el día'} onCerrar={onCerrar} ancho="md:max-w-xl" pie={pie}>
            <div ref={arriba} className="space-y-5 scroll-mt-4">
                {borrador.ofrecido && (
                    <AvisoBorrador texto={`Tenés un pedido sin terminar${nombreBorrador ? ` para ${nombreBorrador}` : ''}`}
                        onSeguir={borrador.seguir} onDescartar={borrador.descartar} />
                )}
                <div>
                    <StepBar paso={indice} total={pasos.length} />
                    <p className={ROTULO}>Paso {indice + 1} de {pasos.length}</p>
                    {/* 1-nov-2026: cargado por el admin a nombre de la empresa, el paso 1 es el cliente DE la empresa */}
                    <h3 className="text-title font-black text-ink leading-tight">{paso === 0 && comoEmpresa ? `¿Para qué cliente de ${comoEmpresa.nombre}?` : TITULOS[paso]}</h3>
                    {paso > 0 && nombreLugar && (
                        <p className="mt-1 flex items-center gap-1.5 text-caption text-muted truncate"><LuMapPin size={13} className="shrink-0" />{nombreLugar}</p>
                    )}
                </div>

                {/* ── 1. Cliente ─────────────────────────────────────────── */}
                {paso === 0 && (!nuevo ? (
                    <section className="space-y-2">
                        {/* Buscador siempre visible (Modo Agua tiene cientos de lugares) */}
                        <div className="relative">
                            <LuSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                            <input className={`${INPUT} pl-10 pr-10`} placeholder="Buscar cliente, dirección o N/S" value={q} onChange={e => setQ(e.target.value)} autoFocus />
                            {q && (
                                <button type="button" onClick={() => setQ('')} aria-label="Borrar búsqueda"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg text-muted flex items-center justify-center active:scale-90"><LuX size={15} /></button>
                            )}
                        </div>
                        {cargando && <p className="text-caption text-muted">Cargando tus clientes…</p>}
                        <div className="space-y-1.5">
                            {filtradas.slice(0, 40).map(s => {
                                const on = s.id === sedeId;
                                return (
                                    <button key={s.id} type="button" onClick={() => elegirLugar(s.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left border-2 active:scale-[0.99] ${on ? 'border-brand-red bg-[rgba(201,52,31,0.06)]' : 'border-transparent bg-chip'}`}>
                                        <LuMapPin size={17} className={on ? 'text-brand-red shrink-0' : 'text-muted shrink-0'} />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-body font-black text-ink truncate">{s.nombre || s.direccion}</span>
                                            <span className="block text-caption text-muted truncate">
                                                {[s.nombre ? s.direccion : null, s.series?.length ? `${s.series.length} equipo${s.series.length === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}
                                            </span>
                                        </span>
                                        {on && <LuCheck size={18} className="text-brand-red shrink-0" />}
                                    </button>
                                );
                            })}
                            {!cargando && q && filtradas.length === 0 && <p className="text-caption text-muted px-1">No hay ninguno con “{q}”.</p>}
                            {filtradas.length > 40 && <p className="text-caption text-muted px-1">Mostrando 40 de {filtradas.length}: escribí para encontrarlo más rápido.</p>}
                        </div>
                        <button type="button" onClick={() => { setNuevo(true); setSedeId(null); setEquipos([]); setCliente(c => ({ ...c, nombre: c.nombre || q.trim() })); }}
                            className="w-full h-11 flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-black/15 dark:border-white/15 text-label font-black text-secondary active:scale-95">
                            <LuUserPlus size={16} /> {q.trim() && filtradas.length === 0 ? `Cargar “${q.trim()}” como cliente nuevo` : 'Cliente nuevo'}
                        </button>
                    </section>
                ) : (
                    <section className="space-y-2">
                        <input className={INPUT} placeholder="Nombre del cliente *" value={cliente.nombre} autoFocus
                            onChange={e => { setCliente(c => ({ ...c, nombre: e.target.value })); setDecision(null); }} />
                        <AvisoParecidos nombre={cliente.nombre} decision={decision} onDecidir={setDecision} onParecidos={setParecidos} />
                        {decision?.tipo !== 'otra' && <CodigoYOrigen c={{ ...cliente, instaladoEl: cliente.instaladoEl || getTodayISO() }} set={setDato} />}
                        <div className="grid grid-cols-[1fr_5.5rem] gap-2">
                            <input className={INPUT} placeholder="Calle *" value={cliente.calle} onChange={e => setCliente(c => ({ ...c, calle: e.target.value }))} />
                            <input className={INPUT} placeholder="Nro" inputMode="numeric" value={cliente.numero} onChange={e => setCliente(c => ({ ...c, numero: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input className={INPUT} placeholder="Piso" value={cliente.piso} onChange={e => setCliente(c => ({ ...c, piso: e.target.value }))} />
                            <input className={INPUT} placeholder="Depto" value={cliente.depto} onChange={e => setCliente(c => ({ ...c, depto: e.target.value }))} />
                        </div>
                        <input className={INPUT} placeholder="Localidad o barrio *" value={cliente.localidad} onChange={e => setCliente(c => ({ ...c, localidad: e.target.value }))} />
                        <UbicacionEnMapa endpoint="/empresa/ubicar" onCambio={setPuntoNuevo}
                            onDireccion={d => setCliente(c => ({ ...c, calle: d.calle || c.calle, numero: d.numero, localidad: d.localidad || c.localidad }))}
                            direccion={[cliente.calle, cliente.numero].filter(Boolean).join(' ') + (cliente.localidad ? `, ${cliente.localidad}` : '')} />
                        <textarea rows={2} value={cliente.notas} onChange={e => setCliente(c => ({ ...c, notas: e.target.value }))}
                            placeholder="A quién preguntar, horario de acceso… (opcional)"
                            className="w-full px-3.5 py-3 rounded-xl bg-chip text-body text-ink outline-none resize-none placeholder:text-muted" />
                        <p className="text-caption text-muted">Queda guardado en tus clientes para el próximo pedido.</p>
                        {sedes.length > 0 && (
                            <button type="button" onClick={() => setNuevo(false)} className="py-1 text-caption font-bold text-secondary underline">Elegir de mis clientes</button>
                        )}
                    </section>
                ))}

                {/* ── 2. Equipos ─────────────────────────────────────────── */}
                {paso === 1 && (
                    <section className="space-y-3">
                        {equiposDelLugar.length > 0 && (
                            <div className="space-y-1.5">
                                <p className={ROTULO}>Equipos de este cliente</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {equiposDelLugar.map(eq => {
                                        const on = equipos.some(e => e.deLista && e.serie === eq.serie);
                                        return (
                                            <button key={eq.serie} type="button" onClick={() => alternarDeLista(eq)} aria-pressed={on}
                                                className={`min-h-9 px-3 py-1.5 rounded-full text-caption font-bold text-left active:scale-95 ${on ? 'bg-[#C9341F] text-white' : 'bg-chip text-secondary'}`}>
                                                {on && <LuCheck size={12} className="inline mr-1 -mt-0.5" />}{eq.serie}{eq.ubicacion ? ` · ${eq.ubicacion}` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {equipos.map((e, i) => (
                            <div key={e.clave} className="rounded-2xl bg-panel p-3 space-y-2.5">
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-body font-black text-ink truncate">
                                            {e.deLista ? `N/S ${e.serie}` : `Equipo ${i + 1}`}
                                        </p>
                                        {e.deLista && (e.modelo || e.ubicacion) && (
                                            <p className="text-caption text-muted truncate">{[e.modelo, e.ubicacion].filter(Boolean).join(' · ')}</p>
                                        )}
                                    </div>
                                    {equipos.length > 1 || equiposDelLugar.length > 0 ? (
                                        <button type="button" onClick={() => setEquipos(es => es.filter(x => x.clave !== e.clave))} aria-label="Quitar equipo"
                                            className="w-8 h-8 shrink-0 rounded-lg bg-chip text-muted flex items-center justify-center active:scale-90"><LuX size={15} /></button>
                                    ) : null}
                                </div>
                                {!e.deLista && (
                                    <div className="space-y-2">
                                        {/* N/S (20-oct-2026): se carga una sola vez. Si lo tenés, acá; si no, lo carga
                                            el técnico al cerrar la visita y queda en el equipo. */}
                                        {e.serie || e.conSerie ? (
                                            <input className={INPUT} placeholder="N/S del equipo" value={e.serie} autoFocus={!e.serie}
                                                onChange={ev => cambiarEquipo(e.clave, { serie: limpiarSerie(ev.target.value) })} />
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <p className="flex-1 text-caption text-muted">Sin N/S: lo carga el técnico al cerrar la visita.</p>
                                                <button type="button" onClick={() => cambiarEquipo(e.clave, { conSerie: true })}
                                                    className="shrink-0 h-9 px-3 rounded-lg bg-chip text-label font-black text-secondary active:scale-95">Tengo el N/S</button>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-2 gap-2">
                                            <input className={INPUT} placeholder="Modelo" value={e.modelo} onChange={ev => cambiarEquipo(e.clave, { modelo: ev.target.value })} />
                                            <input className={INPUT} placeholder="Ubicación" value={e.ubicacion} onChange={ev => cambiarEquipo(e.clave, { ubicacion: ev.target.value })} />
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <p className="text-caption font-bold text-secondary mb-1.5">¿Qué le pasa? *</p>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {MOTIVOS_PEDIDO.map(m => (
                                            <button key={m} type="button" onClick={() => cambiarEquipo(e.clave, { motivo: m })} aria-pressed={e.motivo === m}
                                                className={`min-h-10 px-3 py-1.5 rounded-xl text-label font-black text-left active:scale-95 ${e.motivo === m ? 'bg-[#C9341F] text-white' : 'bg-chip text-secondary'}`}>{m}</button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <button type="button" onClick={() => setEquipos(es => [...es, equipoNuevo({ motivo: ultimoMotivo() })])}
                            className="w-full h-11 flex items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-black/15 dark:border-white/15 text-label font-black text-secondary active:scale-95">
                            <LuPlus size={16} /> {equiposDelLugar.length > 0 ? 'Otro equipo (que no está en la lista)' : 'Otro equipo'}
                        </button>
                        {equipos.length === 0 && <p className="text-caption text-muted">Tocá los equipos que hay que ver.</p>}
                    </section>
                )}

                {/* ── 3. Cuándo + detalles ───────────────────────────────── */}
                {paso === 2 && (
                    <>
                        <ElegirVentanas value={ventanas} onChange={setVentanas} opcional={!!comoEmpresa} />
                        {comoEmpresa && ventanas.length === 0 && (
                            <p className="-mt-2 text-caption text-muted">Opcional: si todavía no sabés cuándo pueden, dejalo vacío y queda en Por agendar.</p>
                        )}

                        {conDia && (
                            <section className="space-y-2">
                                <p className={ROTULO}>¿Para qué día?</p>
                                <ElegirDiaPedido consulta={consultaDia} urgente={urgente} valor={rutaFecha} onElegir={setRutaFecha} onUrgente={() => setUrgente(true)} />
                                {diaSinVentana && !urgente && <p className="text-caption font-bold text-brand-red px-1">Ojo: ese día no está entre los que marcaste que pueden recibirnos.</p>}
                            </section>
                        )}

                        <section className="space-y-2">
                            <p className={ROTULO}>Algo más <span className="normal-case tracking-normal font-bold">(opcional)</span></p>
                            <textarea rows={3} value={detalle} onChange={e => setDetalle(e.target.value)} placeholder="Piso, sector, a quién preguntar, cómo se manifiesta la falla…"
                                className="w-full px-3.5 py-3 rounded-xl bg-chip text-body text-ink outline-none resize-none placeholder:text-muted" />
                        </section>

                        {/* Fotos del problema: el técnico llega sabiendo qué es */}
                        <section className="space-y-2">
                            <p className={ROTULO}>Fotos <span className="normal-case tracking-normal font-bold">(opcional, hasta 4)</span></p>
                            <CargarFotos fotos={fotos} onChange={setFotos} />
                        </section>

                        <button type="button" onClick={() => setUrgente(u => !u)} aria-pressed={urgente}
                            className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left ${urgente ? 'border-brand-red bg-[rgba(201,52,31,0.06)]' : 'border-transparent bg-chip'}`}>
                            <LuTriangleAlert size={18} className={urgente ? 'text-brand-red' : 'text-muted'} />
                            <span className="flex-1">
                                <span className="block text-body font-black text-ink">Es urgente</span>
                                <span className="block text-caption text-muted">El equipo está fuera de servicio o pierde agua</span>
                            </span>
                            <span className={`w-11 h-6 rounded-full p-0.5 transition-colors ${urgente ? 'bg-[#C9341F]' : 'bg-black/15 dark:bg-white/15'}`}>
                                <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${urgente ? 'translate-x-5' : ''}`} />
                            </span>
                        </button>

                        <div className="p-3 rounded-2xl border border-black/10 dark:border-white/10 space-y-1">
                            <p className={ROTULO}>Resumen</p>
                            <p className="text-body font-black text-ink">{nombreLugar}{nuevo && <span className="font-bold text-muted"> · cliente nuevo</span>}</p>
                            {equipos.map(e => (
                                <p key={e.clave} className="text-caption text-secondary">
                                    • {e.serie ? `N/S ${e.serie}` : 'Sin N/S'}{e.ubicacion ? ` · ${e.ubicacion}` : ''} — <b>{e.motivo}</b>
                                </p>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </ModalShell>
    );
}
